import React from 'react';
import { clsx } from 'clsx';

import { HelperProps, helpers } from '../Element/helpers/helpers';
import {
  PolymorphicProps,
  PolymorphicRef
} from '../types/polymorphic/polymorphic.types';
import { getTag } from './Typography.helpers';
import { TypographyVariant } from './Typography.models';

import styles from '../Element/styles/element.module.scss';

const defaultElement = 'p' as const;

type TypographyBaseProps = {
  /** Content of the element. */
  children?: React.ReactNode;
  /** Applies the typography styles with a semantic element. */
  textVariant?: TypographyVariant;
} & HelperProps;

export type TypographyProps<
  T extends React.ElementType = typeof defaultElement
> = PolymorphicProps<T, TypographyBaseProps>;

type TypographyComponent = <
  T extends React.ElementType = typeof defaultElement
>(
  props: PolymorphicProps<T, TypographyProps<T>> & {
    ref?: PolymorphicRef<T>;
  }
) => JSX.Element | null;

export const Typography: TypographyComponent = React.forwardRef(
  <T extends React.ElementType = typeof defaultElement>(
    props: TypographyProps<T>,
    ref: PolymorphicRef<T>
  ) => {
    const {
      textVariant = 'body1',
      renderAs,
      children,
      className = undefined,
      ...allProps
    } = props;
    // Order of precedence:
    // 1. renderAs prop - dev can provide any custom element w/ any text variant style
    // 2. getTag function - dev can default to variant's default element
    // 3. defaultElement  - if all else fails, default to 'p'
    const RenderAs = renderAs ?? getTag(textVariant) ?? defaultElement;
    const processedProps = helpers.base(allProps);

    return (
      <RenderAs
        ref={ref}
        className={clsx(
          className,
          styles[textVariant],
          helpers.classnames(allProps) || undefined
        )}
        {...processedProps}
      >
        {children}
      </RenderAs>
    );
  }
);

export namespace Typography {
  export type Props<T extends React.ElementType = typeof defaultElement> =
    TypographyProps<T>;
}
